import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type {
  WalkthroughSettingsType,
  WalkthroughStepType,
} from '@unifyapps/defs/types/page/walkthrough';
import type { WalkthroughSessionEntity } from '@unifyapps/defs/types/walkthroughSession';
import type { DeviceVariantType } from '@unifyapps/hooks/useScreen/types';
import type { StoreApi } from 'zustand';
import type { WalkthroughMode } from './const';
import type { DriverSlice } from './createDriverSlice';

export type DriverState = {
  steps: WalkthroughStepType[] | null;
  activeIndex?: number; // The step we're navigating to
  displayIndex?: number; // The step UI currently displayed
  __previousStep?: WalkthroughStepType;
  __activeStep?: WalkthroughStepType;
  __displayStep?: WalkthroughStepType; // The step being rendered

  // Tooltip step transition state for deterministic transitions
  tooltipTransitionState: {
    status: 'idle' | 'preparing' | 'finding_element' | 'retrying' | 'scrolling' | 'ready';
    retryCount: number;
    maxRetries: number;
    targetStepIndex: number;
  };
};

export enum WalkthroughProcessState {
  idle = 'idle',
  resolving_conflicts = 'resolving_conflicts',
  preparing_walkthrough = 'preparing_walkthrough',
  walkthrough_active = 'walkthrough_active',
  walkthrough_paused = 'walkthrough_paused',
  error = 'error',
}

export type WalkthroughProcessError = {
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

export interface WalkthroughConductorSlice {
  disabled?: boolean;
  /**
   * Opaque identifier for the walkthrough host instance.
   * In no-code: the interface record id.
   * In generic hosts: any stable identifier (e.g. normalized pathname, route-template key).
   */
  instanceId: string;
  device: DeviceVariantType;
  userId?: string;
  mode: WalkthroughMode;
  /**
   * List of page ids that have been loaded for an interfaceId we are tracking
   */
  loadedPageIds: string[];
  walkthroughs: Record<string, InterfacePageEntity | undefined>;

  /**
   * States when a walkthrough is being played
   */
  activeWalkthroughId: string | null;
  activeSession: WalkthroughSessionEntity | null;
  settings: WalkthroughSettingsType | null;

  /**
   * Process state tracking
   */
  processState: WalkthroughProcessState;
  processError: WalkthroughProcessError | null;
  fetchingPageIds: string[];
  fetchedPageIds: string[];
  attemptCount: number;
  lastTransitionTime: number;

  conductorActions: {
    setActiveSession: (
      updater:
        | ((session: WalkthroughSessionEntity | null) => WalkthroughSessionEntity | null)
        | WalkthroughSessionEntity
        | null,
    ) => void;
    setOnPageLoad: ({ pageId }: { pageId: string }) => void;
    setOnPageUnload: ({ pageId }: { pageId: string }) => void;
    stopWalkthrough: () => void;
    move: (direction: 'next' | 'previous') => void;
    goToStep: (stepIndex: number) => void;

    startFetchingWalkthroughs: (pageId: string) => void;
    markWalkthroughsFetched: (
      pageId: string,
      walkthroughs: Record<string, InterfacePageEntity>,
    ) => void;
    prepareWalkthrough: ({
      walkthroughSteps,
      walkthroughId,
    }: {
      walkthroughSteps: WalkthroughStepType[];
      walkthroughId: string;
    }) => void;
    activateWalkthrough: (initialStepIndex?: number) => void;
    setProcessError: (error: WalkthroughProcessError) => void;
    destroy: () => void;
    transitionTo: (newState: WalkthroughProcessState) => void;
  };
}

export interface WalkthroughManagerStore extends DriverSlice, WalkthroughConductorSlice {}

export type WalkthroughManagerSetType = StoreApi<WalkthroughManagerStore>['setState'];
export type WalkthroughManagerGetType = StoreApi<WalkthroughManagerStore>['getState'];

export type CreateWalkthroughManagerStoreParams = {
  instanceId: string;
  device: DeviceVariantType;
  userId?: string;
  mode: WalkthroughMode;
};

export type WalkthroughContextType = {
  isFirstStep?: boolean;
  isLastStep?: boolean;
  steps?: WalkthroughStepType[] | null;
  activeStepIndex?: number;
  hasNextStep?: boolean;
  hasPreviousStep?: boolean;
  activeStep?: WalkthroughStepType;
  activeWalkthroughId?: string | null;
  activeSession?: WalkthroughSessionEntity | null;
};
